import { Link } from 'react-router-dom';
import type { Comic } from '@/mocks/comics';

interface ComicCardProps {
  comic: Comic;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ComicCard({ comic, variant = 'default' }: ComicCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/comic/${comic.id}`}
        className="group block"
      >
        <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-2.5">
          <img
            src={comic.cover}
            alt={`${comic.title} - 韩国漫画`}
            title={`${comic.title} - 韩国漫画在线阅读`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
          />
          {comic.isNew && (
            <span className="absolute top-2 left-2 px-2 py-0.5 bg-accent-500 text-background-50 text-[10px] font-medium rounded">
              NEW
            </span>
          )}
          {comic.isHot && (
            <span className="absolute top-2 right-2 px-2 py-0.5 bg-red-500 text-background-50 text-[10px] font-medium rounded">
              HOT
            </span>
          )}
        </div>
        <h3 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
          {comic.title}
        </h3>
        <p className="text-xs text-foreground-500 mt-0.5">{comic.author}</p>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/comic/${comic.id}`}
        className="group flex gap-4 bg-background-50 rounded-xl p-4 border border-background-200/60 hover:border-primary-200/60 transition-all cursor-pointer"
      >
        <div className="relative w-24 h-32 flex-shrink-0 rounded-lg overflow-hidden">
          <img
            src={comic.cover}
            alt={`${comic.title} - 韩国漫画`}
            title={`${comic.title} - 韩国漫画在线阅读`}
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="flex flex-col justify-between flex-1 min-w-0">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-base font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors truncate">
                {comic.title}
              </h3>
              {comic.isNew && (
                <span className="px-1.5 py-0.5 bg-accent-500 text-background-50 text-[10px] font-medium rounded flex-shrink-0">
                  NEW
                </span>
              )}
            </div>
            <p className="text-xs text-foreground-500 mb-1">{comic.author}</p>
            <p className="text-xs text-foreground-600 line-clamp-2 leading-relaxed">{comic.description}</p>
          </div>
          <div className="flex items-center gap-3 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-star-fill text-amber-500 text-xs"></i>
              {comic.rating}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {(comic.views / 10000).toFixed(0)}万
            </span>
            <span className="px-2 py-0.5 bg-background-100 rounded text-foreground-600">{comic.status}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/comic/${comic.id}`}
      className="group block cursor-pointer"
      data-product-shop
    >
      <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3">
        <img
          src={comic.cover}
          alt={`${comic.title} - 韩国漫画`}
          title={`${comic.title} - 韩国漫画在线阅读`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
          <span className="text-background-50 text-xs font-medium">立即阅读 →</span>
        </div>
        {comic.isNew && (
          <span className="absolute top-2 left-2 px-2 py-0.5 bg-accent-500 text-background-50 text-[10px] font-medium rounded">
            NEW
          </span>
        )}
        {comic.isHot && (
          <span className="absolute top-2 right-2 px-2 py-0.5 bg-red-500 text-background-50 text-[10px] font-medium rounded">
            HOT
          </span>
        )}
      </div>
      <h3 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
        {comic.title}
      </h3>
      <div className="flex items-center justify-between mt-1">
        <p className="text-xs text-foreground-500">{comic.author}</p>
        <span className="flex items-center gap-0.5 text-xs text-amber-500">
          <i className="ri-star-fill text-[10px]"></i>
          {comic.rating}
        </span>
      </div>
    </Link>
  );
}